import { useContext } from 'react';
import dayjs from 'dayjs';

import { AnalyticsEventNames } from '~/constants/AnalyticsEventMap';
import { Currencies } from '~/constants/currencies';
import { getFrequencyLabel } from '~/constants/depositFrequency';
import { dispatchError } from '~/containers/ErrorContainer/errorEventListener';
import { DepositContext } from '~/context/Deposit/DepositContext';
import { PosthogHelper } from '~/helpers/PosthogHelper';
import { SentryHelper } from '~/helpers/Sentry/SentryHelper';
import { LocalStorageHelper } from '~/helpers/StorageHelper';
import { useTracking } from '~/hooks/tracking/useTracking';
import { useAuthentication } from '~/hooks/useAuthentication';
import { useConfirmDeposit } from '~/hooks/useConfirmDeposit';
import { ModalMultiStepProps } from '~/models/deposits/ModalMultiStepProps';
import { formatCurrency } from '~/utils/currencyFormatter';
import { depositAmountInCents, getDepositAmtWithFeesInCents } from '~/utils/deposits/depositAmountUtils';
import { normalizePaymentMethod } from '~/utils/normalizePaymentMethod';
import { Button } from '~/vinovest-design-system/components';
import { Alert } from '~/vinovest-design-system/components/Form/components/Alert';

export const ReviewTransfer = ({ setStep }: ModalMultiStepProps) => {
    const { depositAmount, minimumDepositAmount, paymentMethod, destinationPortfolio, depositFrequency } =
        useContext(DepositContext);

    const { userId: userID, email } = useAuthentication();
    const source = normalizePaymentMethod(paymentMethod);
    const depositSource = `${source.name} ****${source.lastFour}`;
    const depositFrequencyLabel = getFrequencyLabel(depositFrequency);
    const depositCentsDollars = depositAmount && depositAmountInCents(depositAmount);
    const totalAmountChargedToUserCentDollars = getDepositAmtWithFeesInCents(
        depositCentsDollars,
        source.type,
        source.country
    );
    const { finalizeDeposit, loading, error } = useConfirmDeposit();
    const { captureEvent } = useTracking();

    const depositFeesInDollars = (totalAmountChargedToUserCentDollars - depositCentsDollars) / 100;
    const depositFrequencyChargeDate = dayjs().toISOString();
    const feePercentage = source.country === 'US' || source.country === '' ? '2.9%' : '4.4%';

    const handleTransferSubmit = async (): Promise<void> => {
        try {
            const completePayment = await finalizeDeposit({ ...source, startDate: depositFrequencyChargeDate });
            if (completePayment && !error) {
                setStep(2);

                if (destinationPortfolio == 'whiskey' || destinationPortfolio == 'managed') {
                    LocalStorageHelper.setItem('confirmed-acknowledgements', true);
                }

                PosthogHelper.captureEvent(AnalyticsEventNames.DepositInitiated, {
                    paymentSource: source.type,
                    value: totalAmountChargedToUserCentDollars / 100,
                    depositAmount: totalAmountChargedToUserCentDollars / 100,
                    frequency: depositFrequencyLabel,
                    depositFeesinDollars: depositFeesInDollars,
                    destination: destinationPortfolio as string,
                    entity_type: 'lead',
                    currency: Currencies.USD,
                    userID: userID!,
                    email: email!,
                    records: [
                        {
                            deposit_initiated: true,
                            customer_id: userID!
                        }
                    ]
                });

                return captureEvent(AnalyticsEventNames.DepositInitiated, {
                    paymentSource: source.type,
                    value: totalAmountChargedToUserCentDollars / 100,
                    depositAmount: totalAmountChargedToUserCentDollars / 100,
                    frequency: depositFrequencyLabel,
                    depositFeesinDollars: depositFeesInDollars,
                    destination: destinationPortfolio as string,
                    entity_type: 'lead',
                    currency: Currencies.USD,
                    userID: userID!,
                    email: email!,
                    records: [
                        {
                            deposit_initiated: true,
                            customer_id: userID!
                        }
                    ]
                });
            }

            throw new Error('Unable to complete');
        } catch (completedPaymentError) {
            SentryHelper.captureException(completedPaymentError);
            return dispatchError(completedPaymentError);
        }
    };

    return (
        <div>
            <div className={' flex items-center mb-2'}>
                <p className={'uppercase text-10 text-neutrals-400  w-1/4'}>Amount</p>
                <div
                    className={
                        'border-neutrals-200 border text-12 p-3 w-3/4 rounded flex justify-between text-neutrals-400'
                    }
                >
                    <p className={'text-neutrals-400'}>{formatCurrency(depositAmount, false)}</p>
                    <p className={'text-neutrals-400'}>Min: {formatCurrency(minimumDepositAmount, false)}</p>
                </div>
            </div>
            <div className={'text-neutrals-400 flex items-center mb-2'}>
                <p className={'uppercase text-10 text-neutrals-400 w-1/4'}>From</p>
                <p className={'border-neutrals-200 border text-12 p-3 w-3/4 rounded text-neutrals-400'}>
                    {depositSource}
                </p>
            </div>
            <div className={'items-center flex mb-2'}>
                <p className={'uppercase text-10 text-neutrals-400 w-1/4'}>To</p>
                <p className={'border-neutrals-200 border text-12 p-3 w-3/4 rounded text-neutrals-400 capitalize'}>
                    {destinationPortfolio == 'managed' && 'Wine (Managed)'}
                    {destinationPortfolio == 'whiskey' && 'Whiskey (Managed)'}
                    {destinationPortfolio == 'trading' && 'Trading'}
                </p>
            </div>
            <div className={'items-center flex mb-6'}>
                <p className={'uppercase text-10 text-neutrals-400 w-1/4'}>Frequency</p>
                <p className={'border-neutrals-200 border text-12 p-3 w-3/4 rounded text-neutrals-400'}>
                    {depositFrequencyLabel}
                </p>
            </div>
            {source.type === 'card' && (
                <p className={'text-neutrals-400 text-10 mb-6'}>
                    There will be a {feePercentage} ({formatCurrency(depositFeesInDollars, false)}) processing fee from
                    our processor, Stripe, added to your credit card transfer. You can expect your deposit to arrive
                    immediately.
                </p>
            )}
            {source.type === 'ach' && (
                <p className={'text-neutrals-400 text-10 mb-6'}>
                    There are no processing fees for bank transfers. You can expect your deposit in 2-3 business days.{' '}
                </p>
            )}

            {Boolean(error) && (
                <Alert classnames={'my-4 text-12 '} type={'error'}>
                    Sorry, your payment could not be completed at this time.
                </Alert>
            )}
            <div className={'flex flex-col'}>
                <Button
                    variant={'custom'}
                    type={'submit'}
                    className={`mb-5 text-12 lg:text-14 text-white px-7 py-3 lg:py-4 uppercase rounded loading-button ${
                        loading ? 'loading-button-transition' : ''
                    }`}
                    onClick={handleTransferSubmit}
                >
                    {loading ? 'Submitting' : 'Submit Transfer'}
                </Button>
                <Button variant={'secondary'} onClick={() => setStep(0)}>
                    Back
                </Button>
            </div>
        </div>
    );
};
